import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Grid,
  Table, TableBody, TableCell, TableHead, TableRow, Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getRentalDashboard } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { billingSummary } from '../../utils/rentalBilling';
import {
  money, RentalPageHeader, RentalSection, RentalStatCard, RentalStatusChip, tableSx
} from './rentalUi';

const RentalDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getRentalDashboard();
      setData(res.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load rental dashboard'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onBranch = () => load();
    window.addEventListener('branch-filter-changed', onBranch);
    return () => window.removeEventListener('branch-filter-changed', onBranch);
  }, [load]);

  if (loading) return <CircularProgress />;
  if (error && !data) return <Alert severity="error">{error}</Alert>;

  const c = data.counts || {};
  const m = data.money || {};
  const inv = data.invoices || {};

  const cards = [
    { label: 'Active rentals', value: c.active || 0, to: '/rentals?status=Active', accent: 'success' },
    { label: 'Overdue rentals', value: c.overdue || 0, to: '/rentals?status=overdue', accent: 'error' },
    { label: 'Pending invoices', value: inv.pending_count || 0, extra: money(m.pending), to: '/rentals/pending', accent: 'warning' },
    { label: 'Overdue amount', value: money(m.overdue), to: '/rentals/pending?filter=overdue', accent: 'error' },
    { label: 'Due today', value: inv.due_today_count || 0, extra: money(m.due_today), to: '/rentals/pending', accent: 'info' },
    { label: 'Due this week', value: inv.due_week_count || 0, extra: money(m.due_week), to: '/rentals/pending?filter=due_soon', accent: 'info' },
    { label: 'Collected', value: money(m.collected), to: '/rentals/invoices?filter=paid', accent: 'success' },
    { label: 'Billed', value: money(m.billed), to: '/rentals/invoices', accent: 'primary' },
    { label: 'Outside paid', value: money(m.outside_paid), to: '/rentals/outside', accent: 'warning' },
    { label: 'Outside still to pay', value: money(m.outside_remaining), to: '/rentals/outside', accent: 'warning' }
  ];

  return (
    <Box>
      <RentalPageHeader
        title="Rental dashboard"
        subtitle="Monitor pending payments, due invoices, collections, and outside rent cost."
        actions={(
          <>
            <Button onClick={() => navigate('/rentals/report')}>Reports</Button>
            <Button onClick={() => navigate('/rentals/new')} variant="contained">Create rental</Button>
          </>
        )}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        {cards.map((card) => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={card.label}>
            <RentalStatCard
              label={card.label}
              value={card.value}
              extra={card.extra}
              accent={card.accent}
              onClick={() => navigate(card.to)}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <RentalSection
            title="Overdue invoices"
            action={<Button size="small" onClick={() => navigate('/rentals/pending?filter=overdue')}>All</Button>}
          >
            <Table size="small" sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.overdue_invoices || []).length === 0 ? (
                  <TableRow><TableCell colSpan={4}><Typography color="text.secondary">None overdue.</Typography></TableCell></TableRow>
                ) : (data.overdue_invoices || []).map((i) => (
                  <TableRow key={i.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/rentals/invoices/${i.id}`)}>
                    <TableCell>{i.invoice_number}</TableCell>
                    <TableCell>{i.customer?.name || '—'}</TableCell>
                    <TableCell>{i.due_date}</TableCell>
                    <TableCell align="right">{money(i.balance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </RentalSection>
        </Grid>
        <Grid item xs={12} md={6}>
          <RentalSection
            title="Due this week"
            action={<Button size="small" onClick={() => navigate('/rentals/pending?filter=due_soon')}>All</Button>}
          >
            <Table size="small" sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.due_soon || []).length === 0 ? (
                  <TableRow><TableCell colSpan={4}><Typography color="text.secondary">Nothing due this week.</Typography></TableCell></TableRow>
                ) : (data.due_soon || []).map((i) => (
                  <TableRow key={i.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/rentals/invoices/${i.id}`)}>
                    <TableCell>{i.invoice_number}</TableCell>
                    <TableCell>{i.customer?.name || '—'}</TableCell>
                    <TableCell>{i.due_date}</TableCell>
                    <TableCell align="right">{money(i.balance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </RentalSection>
        </Grid>
        <Grid item xs={12} md={6}>
          <RentalSection
            title="Recent rentals"
            action={<Button size="small" onClick={() => navigate('/rentals')}>All</Button>}
          >
            <Table size="small" sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Rental</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Billing</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.recent_rentals || []).map((r) => (
                  <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/rentals/${r.id}`)}>
                    <TableCell>{r.rental_number}</TableCell>
                    <TableCell>{r.customer?.name || '—'}</TableCell>
                    <TableCell>{billingSummary(r.billing_cycle, r.duration_count)}</TableCell>
                    <TableCell><RentalStatusChip status={r.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </RentalSection>
        </Grid>
        <Grid item xs={12} md={6}>
          <RentalSection
            title="Outside rent"
            action={<Button size="small" onClick={() => navigate('/rentals/outside')}>Profiles</Button>}
          >
            <Table size="small" sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Will pay</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.outside_assets || []).length === 0 ? (
                  <TableRow><TableCell colSpan={3}><Typography color="text.secondary">No outside rentals.</Typography></TableCell></TableRow>
                ) : (data.outside_assets || []).map((a, idx) => (
                  <TableRow
                    key={`${a.serial_number}-${idx}`}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/rentals/outside/${encodeURIComponent(a.serial_number || '')}`)}
                  >
                    <TableCell>
                      {a.product_name}
                      <Typography variant="caption" display="block" color="text.secondary">{a.serial_number}</Typography>
                    </TableCell>
                    <TableCell align="right">{money(a.paid)}</TableCell>
                    <TableCell align="right">{money(a.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </RentalSection>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RentalDashboard;
